import { useState, useEffect } from 'react';
import { 
    Clock, 
    CheckCircle, 
    XCircle, 
    Truck, 
    MapPin, 
    Calendar,
    DollarSign,
    ArrowRight,
    RefreshCw,
    Loader2,
    AlertTriangle,
    Package
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { usePendingConfirmations } from '../contexts/PendingConfirmationsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StagedLoadsPage() {
    const [loads, setLoads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, load: null, action: null });
    const { refresh: refreshPendingCount } = usePendingConfirmations();

    useEffect(() => {
        fetchStagedLoads();
    }, []);

    const fetchStagedLoads = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/staged-loads`, {
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setLoads(data.loads || []);
            } else {
                toast.error('Failed to load staged loads');
            }
        } catch (error) {
            console.error('Failed to fetch staged loads:', error);
            toast.error('Failed to load staged loads');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmLoad = async (loadId) => {
        setActionLoading(loadId);
        try {
            const response = await fetch(`${API_URL}/api/loads/${loadId}/confirm`, {
                method: 'POST',
                credentials: 'include'
            });

            if (response.ok) {
                toast.success('Load confirmed successfully!');
                // Remove from list
                setLoads(prev => prev.filter(l => l.load_id !== loadId));
                refreshPendingCount();
            } else {
                const data = await response.json();
                // Handle structured error responses
                if (data.detail && typeof data.detail === 'object') {
                    const detail = data.detail;
                    if (detail.error_type === 'ratecon_required') {
                        toast.error('Rate Confirmation Required', {
                            description: 'Upload a rate confirmation before booking this load.'
                        });
                    } else if (detail.error_type === 'ratecon_unsigned') {
                        toast.error('Rate Confirmation Not Signed', {
                            description: 'Sign the rate confirmation before booking this load.'
                        });
                    } else if (detail.error_type === 'policy_block') {
                        toast.error('Broker Policy Block', {
                            description: detail.message
                        });
                    } else {
                        toast.error(detail.message || 'Failed to confirm load');
                    }
                } else {
                    toast.error(data.detail || 'Failed to confirm load');
                }
            }
        } catch (error) {
            console.error('Failed to confirm load:', error);
            toast.error('Failed to confirm load');
        } finally {
            setActionLoading(null);
            setConfirmDialog({ open: false, load: null, action: null });
        }
    };

    const handleUnassignLoad = async (loadId) => {
        setActionLoading(loadId);
        try {
            const response = await fetch(`${API_URL}/api/loads/${loadId}/unassign`, {
                method: 'POST',
                credentials: 'include'
            });

            if (response.ok) {
                toast.success('Load unassigned successfully');
                // Remove from list
                setLoads(prev => prev.filter(l => l.load_id !== loadId));
                refreshPendingCount();
            } else {
                const data = await response.json();
                toast.error(data.detail || 'Failed to unassign load');
            }
        } catch (error) {
            console.error('Failed to unassign load:', error);
            toast.error('Failed to unassign load');
        } finally {
            setActionLoading(null);
            setConfirmDialog({ open: false, load: null, action: null });
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="staged-loads-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight uppercase flex items-center gap-3">
                        <Clock className="w-6 h-6 text-amber-500" />
                        Pending Confirmations
                    </h1>
                    <p className="text-zinc-500 text-sm mt-1">
                        Loads awaiting broker confirmation • Confirm or unassign each load individually
                    </p>
                </div>
                <Button 
                    variant="outline"
                    onClick={fetchStagedLoads}
                    disabled={loading}
                    className="border-zinc-700"
                    data-testid="refresh-staged-btn"
                >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {/* Summary Card */}
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                                loads.length === 0 
                                    ? 'bg-green-500/20' 
                                    : loads.length <= 3 
                                        ? 'bg-amber-500/20' 
                                        : 'bg-red-500/20'
                            }`}>
                                <Package className={`w-6 h-6 ${
                                    loads.length === 0 
                                        ? 'text-green-400' 
                                        : loads.length <= 3 
                                            ? 'text-amber-400' 
                                            : 'text-red-400'
                                }`} />
                            </div>
                            <div>
                                <p className="text-sm text-zinc-400">Loads Pending Confirmation</p>
                                <p className={`text-3xl font-mono font-bold ${
                                    loads.length === 0 
                                        ? 'text-green-400' 
                                        : loads.length <= 3 
                                            ? 'text-amber-400' 
                                            : 'text-red-400'
                                }`}>
                                    {loads.length}
                                </p>
                            </div>
                        </div>
                        {loads.length > 0 && (
                            <div className="text-right">
                                <p className="text-sm text-zinc-400">Total Potential Revenue</p>
                                <p className="text-xl font-mono font-bold text-green-400">
                                    {formatCurrency(loads.reduce((sum, l) => sum + (l.gross_pay || 0), 0))}
                                </p>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Loads List */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-10 h-10 text-orange-500 animate-spin mb-4" />
                    <p className="text-zinc-400">Loading staged loads...</p>
                </div>
            ) : loads.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <CheckCircle className="w-12 h-12 text-green-400 mb-4" />
                        <h3 className="text-lg font-medium text-white mb-2">All Clear!</h3>
                        <p className="text-zinc-400 text-center max-w-md">
                            No loads are pending confirmation. Stage a chain from the Trucks page to see loads here.
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3" data-testid="staged-loads-list">
                    {loads.map((load) => (
                        <Card 
                            key={load.load_id} 
                            className="bg-zinc-900/50 border-zinc-800 hover:border-amber-500/30 transition-colors"
                            data-testid={`staged-load-${load.load_id}`}
                        >
                            <CardContent className="p-4">
                                <div className="flex items-start justify-between gap-4">
                                    {/* Load Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-3">
                                            <Badge className="bg-amber-500/20 text-amber-400 border-0">
                                                <Clock className="w-3 h-3 mr-1" />
                                                Pending Confirmation
                                            </Badge>
                                            <span className="text-xs text-zinc-500 font-mono">
                                                {load.load_id}
                                            </span>
                                        </div>

                                        {/* Route */}
                                        <div className="flex items-center gap-2 text-white mb-2">
                                            <MapPin className="w-4 h-4 text-zinc-500" />
                                            <span>{load.origin_city}, {load.origin_state}</span>
                                            <ArrowRight className="w-4 h-4 text-zinc-600" />
                                            <span>{load.destination_city}, {load.destination_state}</span>
                                        </div>

                                        {/* Details Row */}
                                        <div className="flex items-center gap-6 text-sm text-zinc-400">
                                            <div className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4" />
                                                <span>Pickup: {formatDate(load.pickup_date)}</span>
                                            </div>
                                            <span>{load.distance} mi</span>
                                            <span>{load.equipment_type}</span>
                                            {load.assigned_truck_id && (
                                                <div className="flex items-center gap-1 text-orange-400">
                                                    <Truck className="w-4 h-4" />
                                                    <span>{load.assigned_truck_id}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Revenue & Actions */}
                                    <div className="flex flex-col items-end gap-3">
                                        <div className="text-right">
                                            <p className="text-2xl font-mono font-bold text-green-400">
                                                {formatCurrency(load.gross_pay)}
                                            </p>
                                            <p className="text-xs text-zinc-500">
                                                ${(load.gross_pay / load.distance).toFixed(2)}/mi
                                            </p>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setConfirmDialog({ 
                                                    open: true, 
                                                    load, 
                                                    action: 'unassign' 
                                                })}
                                                disabled={actionLoading === load.load_id}
                                                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                                data-testid={`unassign-btn-${load.load_id}`}
                                            >
                                                <XCircle className="w-4 h-4 mr-1" />
                                                Unassign
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => setConfirmDialog({ 
                                                    open: true, 
                                                    load, 
                                                    action: 'confirm' 
                                                })}
                                                disabled={actionLoading === load.load_id}
                                                className="bg-green-600 hover:bg-green-700 text-white"
                                                data-testid={`confirm-btn-${load.load_id}`}
                                            >
                                                {actionLoading === load.load_id ? (
                                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                                ) : (
                                                    <CheckCircle className="w-4 h-4 mr-1" />
                                                )}
                                                Confirm
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, load: null, action: null })}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            {confirmDialog.action === 'confirm' ? (
                                <>
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                    Confirm Load
                                </>
                            ) : (
                                <>
                                    <AlertTriangle className="w-5 h-5 text-red-500" />
                                    Unassign Load
                                </>
                            )}
                        </DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            {confirmDialog.action === 'confirm' ? (
                                <>
                                    Are you sure you want to confirm this load? This indicates the broker has confirmed the booking.
                                </>
                            ) : (
                                <>
                                    Are you sure you want to unassign this load? The load will be returned to "available" status.
                                </>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    {confirmDialog.load && (
                        <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3 my-4">
                            <div className="flex items-center gap-2 text-white mb-2">
                                <span>{confirmDialog.load.origin_city}</span>
                                <ArrowRight className="w-4 h-4 text-zinc-500" />
                                <span>{confirmDialog.load.destination_city}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-zinc-400">{confirmDialog.load.load_id}</span>
                                <span className="text-green-400 font-mono font-semibold">
                                    {formatCurrency(confirmDialog.load.gross_pay)}
                                </span>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setConfirmDialog({ open: false, load: null, action: null })}
                            className="border-zinc-700"
                        >
                            Cancel
                        </Button>
                        {confirmDialog.action === 'confirm' ? (
                            <Button 
                                onClick={() => handleConfirmLoad(confirmDialog.load?.load_id)}
                                disabled={actionLoading === confirmDialog.load?.load_id}
                                className="bg-green-600 hover:bg-green-700 text-white"
                            >
                                {actionLoading === confirmDialog.load?.load_id ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                )}
                                Confirm Load
                            </Button>
                        ) : (
                            <Button 
                                onClick={() => handleUnassignLoad(confirmDialog.load?.load_id)}
                                disabled={actionLoading === confirmDialog.load?.load_id}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                {actionLoading === confirmDialog.load?.load_id ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <XCircle className="w-4 h-4 mr-2" />
                                )}
                                Unassign Load
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
